import React from 'react';
import { Bar } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { Tag, Row, Col } from 'antd';
import '../App.css'; // Make sure to import your CSS file
import LiquidGlass from './LiquidGlass';

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);


const TrainingChart = ({ steps, FTP, isMobile }) => {
    // Check if steps array is empty

    // Create a translucent variant of an rgba() color (same helper idea as HomeTrainerProgress)
    function withAlpha(rgbaColor, alpha) {
        const match = rgbaColor.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)/i);
        if (!match) return rgbaColor;
        const r = parseInt(match[1], 10);
        const g = parseInt(match[2], 10);
        const b = parseInt(match[3], 10);
        const a = typeof alpha === 'number' ? alpha : (match[4] ? parseFloat(match[4]) : 1);
        return `rgba(${r}, ${g}, ${b}, ${a})`;
    }

    const GlassTag = ({ accent, children }) => {
        // Not too transparent: keep the tint readable/legible.
        const tintStrong = withAlpha(accent, 0.34);
        const tintSoft = withAlpha(accent, 0.16);
        const borderCol = withAlpha(accent, 0.55);
        const glow = withAlpha(accent, 0.22);
        return (
            <Tag
                style={{
                    margin: 0, // spacing is handled by Row gutter for strict consistency
                    width: "100%",
                    textAlign: "center",
                    fontWeight: 700,
                    color: "rgba(0,0,0,0.78)",
                    borderRadius: 12,
                    padding: isMobile ? "5px 8px" : "6px 10px",
                    background: `linear-gradient(180deg, ${tintStrong}, ${tintSoft})`,
                    border: `1px solid ${borderCol}`,
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    boxShadow: `0 10px 22px ${glow}, 0 10px 22px rgba(0,0,0,0.10)`,
                }}
            >
                {children}
            </Tag>
        );
    };

    const zones = {
        Z1: `<60%-${Math.round(0.6 * FTP)}W`,
        Z2: `<75%-${Math.round(0.75 * FTP)}W`,
        Z3: `<90%-${Math.round(0.9 * FTP)}W`,
        Z4: `<105%-${Math.round(1.05 * FTP)}W`,
        Z5: `<119%-${Math.round(1.19 * FTP)}W`,
        Z6: `<150%-${Math.round(1.5 * FTP)}W`,
        Z7: `>=150%-${Math.round(1.5 * FTP)}W`,

    }
    const zones2 = {
        Z1: 'Recovery',
        Z2: 'Endurance',
        Z3: 'Tempo',
        Z4: 'Threshold',
        Z5: 'VO₂ Max',
        Z6: 'Anaerobic',
        Z7: 'Ouch...',

    }
    const getZoneindex = (intensity) => {
        if (intensity >= FTP * 1.5) {
            return 6; // Red
        } else if (intensity >= FTP * 1.19) {
            return 5; // Yellow
        } else if (intensity >= FTP * 1.05) {
            return 4; // Yellow
        } else if (intensity >= FTP * 0.9) {
            return 3; // Yellow
        } else if (intensity >= FTP * 0.75) {
            return 2; // Yellow
        }
        else if (intensity >= FTP * 0.6) {
            return 1; // blue
        } else {
            return 0;
        }
    };
    function formatTime(seconds) {
        let reverse = false
        if (seconds < 0) {
            seconds = -seconds
            reverse = true
        }
        const minutes = Math.floor(seconds / 60); // Get the full minutes
        const remainingSeconds = seconds % 60;    // Get the remaining seconds
        // Return the formatted time string
        // Add a leading zero to seconds if less than 10
        const timeStr = reverse ? `- ${minutes}'${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}` : `${minutes}'${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
        return timeStr
    }
    // Format seconds to m:ss for axis tick labels
    function formatTimeLabel(seconds) {
        if (seconds == null || isNaN(seconds)) return '';
        const totalSeconds = Math.max(0, Math.round(Number(seconds)));
        const minutes = Math.floor(totalSeconds / 60);
        const remainingSeconds = totalSeconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    }
    const getColor = (intensity) => {
        if (intensity >= FTP * 1.5) {
            return 'rgba(128, 0, 128, 1)';
        } else if (intensity >= FTP * 1.19) {
            return 'rgba(255, 0, 0, 1)';
        } else if (intensity >= FTP * 1.05) {
            return 'rgba(255, 165, 0, 1)';
        } else if (intensity >= FTP * 0.9) {
            return 'rgba(255, 215, 0, 1)';
        } else if (intensity >= FTP * 0.75) {
            return 'rgba(0, 128, 0, 1)';
        }
        else if (intensity > FTP * 0.6) {
            return 'rgba(153, 204, 255, 1)';
        } else {
            return 'rgba(128, 128, 128, 1)';
        }
    };
    let cumulativeDuration = 0.5;

    let data = [];

    steps.forEach((step, index) => {
        for (let i = 0; i < step.duration; i++) {
            const x = cumulativeDuration
            data.push({
                x: x, // Position x is the index plus fraction for each minute
                y: step.watt,
                color: getColor(step.watt),
            });
            cumulativeDuration += 1

        }
    });


    const chartData = {
        labels: data.map(substep => substep.x),
        datasets: [{
            label: 'Watts',
            data: data,
            borderWidth: 0.5,
            backgroundColor: data.map(substep => substep.color),
            //borderColor: data.map(substep => substep.color),
            barPercentage: 1.0,
            categoryPercentage: 1.0
        }]
    };

    const options = {
        indexAxis: 'x',
        scales: {
            x: {
                type: 'linear',
                title: {
                    display: true,
                    text: 'Duration (min:sec)'
                },
                position: 'bottom',
                stacked: true,
                ticks: {
                    callback: (value) => formatTimeLabel(value)
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Watts'
                }
            }
        },
        plugins: {
            legend: {
                display: false
            },
            title: {
                display: true,
                text: 'Training Watts over time (min:sec)'
            }

        },
        // barThickness: "100%"
    };

    //const legendWatt = steps.map(x => x.watt)
    const legendWatt = [0.5 * FTP, 0.7 * FTP, 0.8 * FTP, 1 * FTP, 1.1 * FTP, 1.2 * FTP, 1.7 * FTP]
    const zoneDuration = [0, 0, 0, 0, 0, 0, 0];
    steps.forEach((x) => {
        zoneDuration[getZoneindex(x.watt)] += x.duration
    })
    return <LiquidGlass className="liquid-chart" style={{ padding: 16 }}>
        <Bar data={chartData} options={options} />
        <div style={{ marginTop: "12px" }}>
            <Row gutter={[8, 8]}>
                <Col span={2}></Col>
                {legendWatt.map((x) => getColor(x)).map((accent, i) => (
                    <Col key={`zone-tag-${i}`} span={3}>
                        <GlassTag accent={accent}>
                            {steps.length > 0 ? `Z${i + 1} : ${formatTime(zoneDuration[i])}` : `Z${i + 1}`}
                        </GlassTag>
                    </Col>
                ))}
            </Row>
            <Row gutter={[8, 8]} style={{ marginTop: "8px", marginLeft: "10px" }} >
                <Col span={2}></Col>
                {legendWatt.map((x) => getColor(x)).map((_, i) => (
                    <Col key={`zone-name-${i}`} span={3}>{zones2[`Z${i + 1}`]}</Col>
                ))}
            </Row>
            <Row gutter={[8, 8]} style={{ marginTop: "4px", marginLeft: "10px" }} >
                <Col span={2}></Col>
                {legendWatt.map((x) => getColor(x)).map((_, i) => (
                    <Col key={`zone-range-${i}`} span={3}>{zones[`Z${i + 1}`]}</Col>
                ))}
            </Row>
            <Row style={{ marginTop: "12px" }}>
                <Col span={2}></Col>
                <i>More info about <a href='https://www.trainerroad.com/blog/cycling-power-zones-training-zones-explained/#:~:text=Cycling%20intensity%20levels%20are%20commonly,is%20crucial%20to%20proper%20training.' target="_blank" rel="noreferrer">cycling power zones</a></i>
            </Row>
        </div>
    </LiquidGlass>;
}

export default TrainingChart;

